import React, { useState, useEffect } from "react";
import { Card, Form, Input, InputNumber, Select, Switch, Button, Space, message, Divider } from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  useFetchProductQuery,
  useFetchCategoriesQuery,
  useFetchAllergensQuery,
  useUpdateProductMutation,
  useCreateProductMutation,
} from "./label.service";

const { TextArea } = Input;
const { Option } = Select;

export default function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isEdit = !!id;

  const { data: productData, isLoading: productLoading } = useFetchProductQuery(id, { skip: !isEdit });
  const { data: categoriesData } = useFetchCategoriesQuery();
  const { data: allergensData } = useFetchAllergensQuery();
  const [updateProduct, { isLoading: isUpdating }] = useUpdateProductMutation();
  const [createProduct, { isLoading: isCreating }] = useCreateProductMutation();

  const product = productData?.data?.product;
  const categories = categoriesData?.data?.categories || [];
  const allergens = allergensData?.data?.allergens || [];

  useEffect(() => {
    if (isEdit && product) {
      form.setFieldsValue({
        name: product.name,
        categoryId: product.categoryId?._id,
        description: product.description,
        storageLocation: product.storageLocation,
        isActive: product.isActive,
        expiryDays: product.customExpiryRule?.days || 2,
        allergens: product.allergens?.map(a => a.allergenId?._id) || [],
      });
    } else {
      form.setFieldsValue({
        isActive: true,
        expiryDays: 2,
      });
    }
  }, [product, isEdit, form]);

  const handleSubmit = async (values) => {
    try {
      const productData = {
        name: values.name,
        categoryId: values.categoryId,
        description: values.description,
        storageLocation: values.storageLocation,
        isActive: values.isActive,
        customExpiryRule: {
          enabled: true,
          days: values.expiryDays,
          unit: "Days"
        },
        allergens: values.allergens?.map(allergenId => ({ allergenId })) || [],
      };

      if (isEdit) {
        await updateProduct({ id, data: productData }).unwrap();
        message.success("Product updated successfully");
      } else {
        await createProduct(productData).unwrap();
        message.success("Product created successfully");
      }
      navigate("/label/categories");
    } catch (error) {
      message.error(error?.data?.message || "Operation failed");
    }
  };

  if (productLoading) return <Card loading />;

  return (
    <Card
      title={
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/label/categories")} />
          <span style={{ fontSize: "18px", fontWeight: "bold" }}>
            {isEdit ? "Edit Product" : "Add Product"}
          </span>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          name="name"
          label="Product Name"
          rules={[{ required: true, message: "Please enter product name" }]}
        >
          <Input placeholder="e.g., SEA BREAM, COD, HAKE" />
        </Form.Item>

        <Form.Item
          name="expiryDays"
          label="Number of days best before"
          rules={[{ required: true, message: "Please enter expiry days" }]}
        >
          <InputNumber min={1} max={365} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item name="storageLocation" label="Storage Location">
          <Select
            placeholder="Select storage location"
            options={[
              { label: "Fridge", value: "Fridge" },
              { label: "Freezer", value: "Freezer" },
              { label: "Ambient", value: "Ambient" },
            ]}
          />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <TextArea rows={3} />
        </Form.Item>

        <Form.Item name="allergens" label="Allergens">
          <Select mode="multiple" placeholder="Select allergens">
            {allergens.map(allergen => (
              <Option key={allergen._id} value={allergen._id}>
                {allergen.name} ({allergen.type})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="categoryId"
          label="Category"
          rules={[{ required: true, message: "Please select category" }]}
          hidden={isEdit}
        >
          <Select placeholder="Select category">
            {categories.map(cat => (
              <Option key={cat._id} value={cat._id}>{cat.name}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="isActive" label="Active" valuePropName="checked">
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>

        <Divider />

        <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
          <Space>
            <Button onClick={() => navigate("/label/categories")}>Cancel</Button>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={isUpdating || isCreating}>
              {isEdit ? "Update Product" : "Create Product"}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
}